/**
 * IntermediaryTab — is_intermediary checkbox; nếu bật, shipper_code required.
 * BR-LP-3 (theo SOP): me_or_ib KHÔNG ép buộc shipper field.
 */
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
} from '@mui/material'
import { Controller, type Control, type FieldErrors } from 'react-hook-form'

import { useLookupVendorsQuery } from '@/store/api/lookupsApi'
import type { LocalPFormValues } from './types'

export interface IntermediaryTabProps {
  control: Control<LocalPFormValues>
  errors: FieldErrors<LocalPFormValues>
  isIntermediary: boolean
}

export function IntermediaryTab({ control, errors, isIntermediary }: IntermediaryTabProps) {
  const { data: vendors = [] } = useLookupVendorsQuery({ q: '' })

  return (
    <Box display="flex" flexDirection="column" gap={2} pt={1}>
      <Controller
        control={control}
        name="is_intermediary"
        render={({ field }) => (
          <FormControlLabel
            control={
              <Checkbox
                checked={!!field.value}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            }
            label="Có người mua trung gian (Intermediary)"
          />
        )}
      />

      {isIntermediary && (
        <>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Controller
              control={control}
              name="shipper_code"
              render={({ field }) => {
                const selected =
                  vendors.find((v) => v.code === field.value) ??
                  (field.value
                    ? { code: field.value, name: '', short_name: null, vat_code: null }
                    : null)
                return (
                  <Autocomplete
                    size="small"
                    sx={{ flex: '2 1 320px' }}
                    options={vendors}
                    value={selected}
                    onChange={(_e, v) => field.onChange(v?.code ?? '')}
                    getOptionLabel={(opt) =>
                      opt ? `${opt.code} — ${opt.name ?? ''}` : ''
                    }
                    isOptionEqualToValue={(o, v) => o.code === v.code}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Shipper (intermediary) *"
                        error={!!errors.shipper_code}
                        helperText={errors.shipper_code?.message}
                      />
                    )}
                  />
                )
              }}
            />
            <Controller
              control={control}
              name="contract_shipper_intermediary"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Contract Shipper Intermediary"
                  size="small"
                  sx={{ flex: '1 1 240px' }}
                  error={!!errors.contract_shipper_intermediary}
                  helperText={errors.contract_shipper_intermediary?.message}
                />
              )}
            />
          </Box>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Controller
              control={control}
              name="outright_shipper_intermediary"
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value === '' ? '' : field.value}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  label="Outright Shipper Intermediary"
                  type="number"
                  size="small"
                  sx={{ flex: '1 1 220px' }}
                  error={!!errors.outright_shipper_intermediary}
                  helperText={errors.outright_shipper_intermediary?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="ptbfdiffs_shipper_intermediary"
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value === '' ? '' : field.value}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  label="PTBF Diffs Shipper Intermediary"
                  type="number"
                  size="small"
                  sx={{ flex: '1 1 240px' }}
                  error={!!errors.ptbfdiffs_shipper_intermediary}
                  helperText={errors.ptbfdiffs_shipper_intermediary?.message}
                />
              )}
            />
          </Box>
        </>
      )}
    </Box>
  )
}

export default IntermediaryTab
